import { Dialog } from "@headlessui/react"
import { FC, useEffect, useState } from "react"
import {
  ANALYSIS_FILTER,
  errorFilledToastOptions,
  PAYMENT_PERIOD_TYPE,
  PAYMENT_PERIOD_TYPE_ID,
  tableCellClasses
} from "../../../../../constants"
import { X } from "heroicons-react"
import * as React from "react"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { userPayments } from "../../../../../apis"
import { toast } from "react-toastify"
import { getPaymentPeriodOptions, mapListToCamelCase } from "../../../../../utility"

type ApprovePaymentModalProps = {
  selectedIds: number[]
  open: boolean
  userPaymentPeriodId: number | undefined
  selectedPeriod: string
  selectedUsername?: string
  selectedUserId?: number
  onClose: () => void
  approve: (paymentDay: string, selectedDate: string[], deductFromAdvance: boolean) => void
  selectedFilter: string
  selectedPaymentUser?: KSelectOption | undefined
}

const quarterMap = {
  "01": "q1",
  "02": "q1",
  "03": "q1",
  "04": "q2",
  "05": "q2",
  "06": "q2",
  "07": "q3",
  "08": "q3",
  "09": "q3",
  10: "q4",
  11: "q4",
  12: "q4"
}

const monthToQuarter = {
  q1: ["01", "02", "03"],
  q2: ["04", "05", "06"],
  q3: ["07", "08", "09"],
  q4: ["10", "11", "12"]
}

interface PaymentAmounts {
  advancePayment: number
  regularPayment: number
  currency: string
}

const ApprovePaymentModal: FC<ApprovePaymentModalProps> = (props) => {
  const { onClose, open, selectedIds } = props
  const [dateList, setDateList] = useState<KSelectOption[]>([])
  const [selectedDate, setSelectedDate] = useState<string>(props.selectedPeriod)
  const [paymentDay, setPaymentDay] = useState<string>("")
  const defaultValueForDeduction = false
  const [deductFromAdvance, setDeductFromAdvance] = useState<boolean>(defaultValueForDeduction)
  const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmounts[]>([])
  const [isDeductionPossible, setIsDeductionPossible] = useState<boolean>(false)

  useEffect(() => {
    setDateList(getPaymentPeriodOptions(props.userPaymentPeriodId))
  }, [props.userPaymentPeriodId])

  const calculatePaymentAmounts = async () => {
    const isUserBalance = props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
    if (isUserBalance && !props.selectedPaymentUser) {
      return
    }

    if (!isUserBalance && !props.selectedUserId) {
      return
    }

    const date: string[] = createDateArray()
    const requestBody = {
      date,
      user_id: isUserBalance ? props.selectedPaymentUser?.value : props.selectedUserId,
      payment_day: paymentDay
    }
    try {
      const userBalanceParam = isUserBalance ? "&approve_all=1" : ""
      const selectedIdsParam = selectedIds?.length > 0 ? `&selected_ids=${selectedIds.join(",")}` : ""
      const response = await userPayments(
        `/calculate_payment_amounts/?t=1${selectedIdsParam}${userBalanceParam}`,
        "post",
        requestBody
      )
      if (response.status === 200) {
        const calculatedPaymentAmounts: PaymentAmounts[] = mapListToCamelCase(response.data.results)

        const deductionPossible = calculatedPaymentAmounts.some((paymentAmount) => paymentAmount.advancePayment > 0)

        setPaymentAmounts(calculatedPaymentAmounts)
        setIsDeductionPossible(deductionPossible)
      } else {
        toast.error("Error on calculations", errorFilledToastOptions)
      }
    } catch (err) {
      toast.error("Error on calculations", errorFilledToastOptions)
    }
  }

  useEffect(() => {
    if (open && paymentDay.length === 8) {
      calculatePaymentAmounts()
    }
  }, [selectedDate, open, paymentDay])

  const resetSelections = () => {
    setDeductFromAdvance(defaultValueForDeduction)
    setPaymentDay("")

    if (props.userPaymentPeriodId === PAYMENT_PERIOD_TYPE_ID.QUARTERLY && props.selectedPeriod.length === 6) {
      const month = props.selectedPeriod.substring(4, 6)
      const year = props.selectedPeriod.substring(0, 4)
      setSelectedDate(`${year}${quarterMap[month]}`)
      return
    }
    setSelectedDate(props.selectedPeriod)
  }

  useEffect(() => {
    if (!props.userPaymentPeriodId) {
      return
    }

    resetSelections()
  }, [props.selectedPeriod, props.userPaymentPeriodId])

  const createDateArray = () => {
    const date: string[] = []
    if (selectedDate.includes("q")) {
      const year = selectedDate.substring(0, 4)
      monthToQuarter[selectedDate.substring(4, 6)].forEach((month: string) => date.push(`${year}${month}`))
    } else {
      date.push(selectedDate)
    }

    return date
  }

  const approvePayment = () => {
    const date: string[] = createDateArray()
    props.approve(paymentDay, date, deductFromAdvance)
    resetSelections()
  }

  const onCloseClick = () => {
    props.onClose()
    resetSelections()
  }

  const onApproveClick = () => {
    approvePayment()
    onClose()
  }

  return (
    <Dialog open={open} onClose={onCloseClick} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 rounded-lg">
        <div className="bg-white elev-b-sm rounded-lg" style={{ minWidth: "470px" }}>
          <div
            className={`bg-gray-50 text-xs text-gray-500 font-medium rounded-t-lg
              uppercase border-b flex justify-between ${tableCellClasses.sm}`}
          >
            <KTitleSpan
              text="APPROVE PAYMENT"
              color="#111"
              fontWeight={500}
              fontSize={14}
              lineHeight="20px"
              letterSpacing="0.2px"
            />
            <X className="h-4 mr-[-6px] cursor-pointer" onClick={onCloseClick} />
          </div>
          <div className="px-4 pt-4">
            <div className="flex flex-row gap-1 mb-6">
              <KSpan text="Selected User:" color="#000" fontSize={16} lineHeight="20px" />
              <KSpan
                text={
                  props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
                    ? props.selectedPaymentUser?.label || ""
                    : props.selectedUsername || ""
                }
                color="#000"
                fontWeight={600}
                fontSize={16}
                lineHeight="20px"
              />
            </div>
            <KSpan text="Payment Day (YYYYMMDD) - Accounting Period" color="#000" fontSize={16} lineHeight="20px" />

            <div className="mt-2">
              <KInput
                value={paymentDay}
                placeholder={"Ex: 20230123"}
                padding="14px"
                onChange={(text) => {
                  if (text.length <= 8) {
                    setPaymentDay(text)
                  }
                }}
              />
            </div>
          </div>
          {paymentDay.length === 8 && (
            <div className="px-4">
              {isDeductionPossible && (
                <div className="mt-8 flex flex-row gap-2 items-start max-w-[320px]">
                  <input
                    type="checkbox"
                    value=""
                    className="mt-1 cursor-pointer"
                    checked={deductFromAdvance}
                    style={{
                      accentColor: "#000"
                    }}
                    onChange={() => {
                      setDeductFromAdvance(!deductFromAdvance)
                    }}
                  />
                  <KSpan text="Deduct from advance" color="#000" fontSize={16} lineHeight="20px" />
                </div>
              )}
              <div className="flex flex-col gap-1 mt-8">
                {paymentAmounts.length > 0 ? (
                  paymentAmounts.map((paymentAmount, index) => {
                    return (
                      <div key={`payment-amount-${index}`} className="flex items-center gap-2">
                        {isDeductionPossible && (
                          <div className="flex flex-row gap-1">
                            <KSpan
                              text={`Advance Deduction:`}
                              color="#000"
                              fontWeight={600}
                              fontSize={14}
                              lineHeight="20px"
                            />
                            <KSpan
                              text={`${deductFromAdvance ? paymentAmount.advancePayment.toFixed(3) : "0"} ${
                                paymentAmount.currency
                              },`}
                              color="#000"
                              fontWeight={400}
                              fontSize={14}
                              lineHeight="20px"
                            />
                          </div>
                        )}
                        <div className="flex flex-row gap-1">
                          <KSpan
                            text={`Payout Amount:`}
                            color="#000"
                            fontWeight={600}
                            fontSize={14}
                            lineHeight="20px"
                          />
                          <KSpan
                            text={` ${
                              deductFromAdvance
                                ? paymentAmount.regularPayment.toFixed(3)
                                : (paymentAmount.regularPayment + paymentAmount.advancePayment).toFixed(3)
                            } ${paymentAmount.currency} `}
                            color="#000"
                            fontWeight={400}
                            fontSize={14}
                            lineHeight="20px"
                          />
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="flex flex-row gap-1">
                    <KSpan text={`Payout Amount:`} color="#000" fontWeight={600} fontSize={14} lineHeight="20px" />
                    <KSpan text={`0`} color="#000" fontWeight={400} fontSize={14} lineHeight="20px" />
                  </div>
                )}
              </div>

              <div className="mt-12 mb-2 flex flex-row gap-1 items-center">
                <KSpan text=" Payment Period:" color="#000" fontSize={16} lineHeight="20px" />
                <KSpan
                  text={
                    props.selectedFilter === ANALYSIS_FILTER.USER_BALANCE
                      ? "ALL"
                      : props.userPaymentPeriodId
                      ? PAYMENT_PERIOD_TYPE[props.userPaymentPeriodId]
                      : "-"
                  }
                  color="#000"
                  fontWeight={600}
                  fontSize={16}
                  lineHeight="20px"
                />
              </div>
              <div className="mb-6">
                {props.selectedFilter !== ANALYSIS_FILTER.USER_BALANCE && (
                  <KDropdown
                    width={200}
                    padding="14px"
                    defaultValuePrimitive={selectedDate}
                    rightIcon="/analytics_icons/caret-down-new.svg"
                    enableIndicator={true}
                    options={dateList}
                    onSelect={(selectedOption) => {
                      const selected = selectedOption as KSelectOption
                      if (!selected.value2) return
                      setSelectedDate(selected.value2)
                    }}
                    selected={dateList.find((option) => option.value2 === selectedDate)}
                  />
                )}
              </div>
            </div>
          )}
          <div className="flex gap-4 p-4 px-8 mt-3">
            <KButton
              text={"Cancel"}
              background="#F7F7F7"
              textColor="#000"
              height="48px"
              padding="14px"
              onClick={onCloseClick}
            />
            <KButton
              text={"Approve"}
              background="#000"
              textColor="#FFF"
              height="48px"
              disabled={paymentDay.length !== 8}
              padding="14px"
              onClick={onApproveClick}
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ApprovePaymentModal
